import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useRef, useState } from 'react';

import AllFields from '../../../user-management/user-details/gmail-settings/signatures/all-fields';
import { CustomSchemaEntity } from '../../../../../types';
import { Editor as TinyMCEEditor } from 'tinymce';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  getGmailSendAsForSignatures,
  getSignatureWithValues,
  getUser,
  UserKeys,
} from '../../../../api/users-api';
import TextEditor from '../../../../shared/editor/text-editor';
import UsersAutocomplete from '../../../../shared/users-autocomplete';
import HtmlPreview from '../../../../shared/html-preview';
import { SignatureTemplateFormValues } from '../signature-templates-form-page';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Grid from '@mui/material/Grid';

interface StepSignatureTemplateCreatorProps {
  control: Control<SignatureTemplateFormValues>;
  watch: UseFormWatch<SignatureTemplateFormValues>;
  setValue: UseFormSetValue<SignatureTemplateFormValues>;
  errors: FieldErrors<SignatureTemplateFormValues>;
  customSchemas: CustomSchemaEntity[] | undefined;
  loggedInUser: string;
}

export default function StepSignatureTemplateCreator({
  control,
  watch,
  customSchemas,
  loggedInUser,
  errors,
}: StepSignatureTemplateCreatorProps) {
  const editorRef = useRef<TinyMCEEditor | null>(null);
  const htmlTemplate = watch('templateHtml') || '';
  const [autoCompleteValue, setAutoCompleteValue] = useState(loggedInUser);

  const { data: user } = useQuery({
    queryKey: [UserKeys.User, autoCompleteValue],
    queryFn: () => getUser(autoCompleteValue),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: !!autoCompleteValue,
  });

  const { data: sendAsAccounts } = useQuery({
    queryKey: [UserKeys.SendAsAccounts, autoCompleteValue],
    queryFn: () => getGmailSendAsForSignatures(autoCompleteValue),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!autoCompleteValue,
    meta: {
      errorMessage:
        'There was an issue loading send as accounts. Please refresh and try again.',
    },
  });

  const currentSendAsEmail = sendAsAccounts?.find(
    (account) => account.isPrimary
  )!.sendAsEmail;

  const currentSendAsAccount = sendAsAccounts?.find(
    (account) => account.sendAsEmail === currentSendAsEmail
  );

  const { data } = useQuery({
    queryKey: [
      UserKeys.SignatureValue,
      loggedInUser,
      htmlTemplate,
      user,
      currentSendAsAccount,
    ],
    queryFn: () =>
      getSignatureWithValues({
        template: htmlTemplate,
        user: user!,
        sendAs: currentSendAsAccount!,
        requestTime: new Date().getTime(),
      }),
    placeholderData: keepPreviousData,
    enabled: !!user && !!sendAsAccounts,
  });

  return (
    <Box
      sx={{
        viewTransitionName: 'steps',
      }}
      width={'100%'}
      height={'100%'}
    >
      <Grid
        container
        direction="column"
        size={{ xs: 12 }}
        sx={{ height: '100%' }}
      >
        <Grid sx={{ flexShrink: 0 }}>
          <Stack mt={-1} spacing={1}>
            {customSchemas ? (
              <AllFields customSchemas={customSchemas} editorRef={editorRef} />
            ) : (
              <Skeleton
                variant="rounded"
                width={400}
                height={40}
                sx={{ mt: 1 }}
              />
            )}
          </Stack>
        </Grid>
        <Grid sx={{ flexShrink: 0, mt: 1, maxWidth: '400px' }}>
          {autoCompleteValue ? (
            <Box sx={{ height: 40 }}>
              <Chip
                label={autoCompleteValue}
                onDelete={() => setAutoCompleteValue('')}
                size="small"
              />
            </Box>
          ) : (
            <Box sx={{ mb: 2 }}>
              <UsersAutocomplete
                autocompleteValue={autoCompleteValue}
                setAutoCompleteValue={setAutoCompleteValue}
                size="small"
              />
            </Box>
          )}
        </Grid>
        <Grid
          container
          size={{ xs: 12 }}
          sx={{ flex: 1, minHeight: 0 }}
          spacing={2}
        >
          <Grid size={{ xs: 6 }} sx={{ height: '100%' }}>
            <Box
              sx={
                errors.templateHtml
                  ? {
                      width: '100%',
                      height: '100%',
                      border: (theme) =>
                        `1px solid ${theme.palette.error.main}`,
                      borderRadius: 3,
                    }
                  : { width: '100%', height: '100%' }
              }
            >
              <Controller
                name="templateHtml"
                control={control}
                render={({ field }) => (
                  <TextEditor
                    editorRef={editorRef}
                    content={field.value!}
                    setContent={field.onChange}
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 6 }} sx={{ height: '100%' }}>
            <HtmlPreview html={data?.populatedTemplate || ''} height={'100%'} />
          </Grid>
        </Grid>
        {errors.templateHtml && (
          <Typography color="error" variant="caption" sx={{ ml: '14px' }}>
            {errors.templateHtml.message}
          </Typography>
        )}
      </Grid>
    </Box>
  );
}
